import { Layout as AntLayout } from 'antd';
import React, { useEffect, useState } from "react";
import { LayoutComponent, ReduxState, setSidebarVisibility, useLogout } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from "react-router-dom";
import Footer from './Footer';
import Notification from './Notification';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { Helmet } from 'react-helmet';
import useGetCurrentResource from './useGetCurrentResource';
import { ErrorBoundary } from "react-error-boundary";
import { Error } from './Error';
import './TopBar.css';

const { Content } = AntLayout;

/**
 * Whether the nav rail is showing. Remembered, because it is a working
 * preference rather than a per-page state: someone who put the rail away to
 * read a wide table wants it away on the next page too, and after a reload.
 */
const NAV_OPEN_KEY = 'nav_open';

/** below this the rail is not on the page at all — the drawer stands in for it */
const RAIL_HIDDEN = '(max-width: 991.98px)';

const Layout: LayoutComponent = ({
    children
}) => {
    const dispatch = useDispatch();
    const currentResource = useGetCurrentResource();
    const logout = useLogout();
    const sidebarOpen = useSelector((state: ReduxState) => state.admin.ui.sidebarOpen);
    const [navOpen, setNavOpen] = useState(() => localStorage.getItem(NAV_OPEN_KEY) !== 'false');
    let { pathname } = useLocation();

    useEffect(() => {
        localStorage.setItem(NAV_OPEN_KEY, String(navOpen));
    }, [navOpen]);

    /**
     * One control for the nav, whichever form it is in. Where the rail is on
     * the page it opens and closes it; where it is not, the same click opens
     * the drawer that replaces it — react-admin's own sidebar flag, which is
     * what that drawer already reads.
     */
    const toggleNav = () => {
        if (window.matchMedia(RAIL_HIDDEN).matches) dispatch(setSidebarVisibility(!sidebarOpen));
        else setNavOpen((open) => !open);
    };

    return (
        <>
            <div className={`layout-v2${navOpen ? '' : ' layout-v2--nav-off'}`}>
                <Helmet>
                    <title>{currentResource} - NetOrca</title>
                </Helmet>
                <TopBar
                    pathName={pathname}
                    navOpen={navOpen}
                    toggleNav={toggleNav}
                    logout={logout}
                />
                <div className="layout-v2-body">
                    <AntLayout className="layout-dashboard">
                        <Sidebar />
                        <AntLayout>
                            <ErrorBoundary
                                fallbackRender={({ error, resetErrorBoundary }) => (
                                    <Error
                                        error={error}
                                        resetErrorBoundary={resetErrorBoundary}
                                    />
                                )}
                            >
                                <Content className="content-ant layout-v2-content">
                                    {children}
                                </Content>
                            </ErrorBoundary>
                            <Footer />
                        </AntLayout>
                    </AntLayout>
                </div>
                <Notification />
            </div>
        </>
    );
};

export default Layout;
